/*
 * aa-moon-core.js — GC28 (Fable)
 * Moteur 2D PUR du studio lampe lune : pipeline photo + FRONTIÈRE DE RÉFÉRENCE.
 * Doctrine VERBATIM adaptée DSers : l'image transmise au fournisseur (_aa_photo) est
 * EXACTEMENT celle produite ici par renderReference() — la même que celle affichée
 * dans l'aperçu (3D ou repli 2D). Aucun re-traitement serveur, jamais.
 * Zéro dépendance DOM imposée (canvas uniquement) → re-rendu headless possible.
 */
(function () {
  'use strict';

  var MIN_PX = 600;          // côté court minimal accepté (AM-C2 : refus + message, jamais d'impression pixelisée)
  var REF_PX = 1200;         // référence carrée transmise (≈ 10 cm @ 300 DPI)
  var TEX_W = 2048, TEX_H = 1024; // textures équirectangulaires (albédo NASA = même format)

  /* Zone photo sur la sphère (face avant, centrée équateur) — en degrés.
     LES DEUX VALEURS DOIVENT RESTER EGALES. Sur une sphere, un disque centre a l'equateur
     couvre le meme angle en longitude et en latitude : des que lonSpan != latSpan, le
     medaillon est un ovale couche, et ca se voit immediatement. Il l'a toujours ete —
     100x84 donnait un rapport largeur/hauteur de 1,12 ; 150x122 de 1,09 en debordant du
     disque (95 % de large). Mesure : `banc/medaillon-rond.mjs`.
     104 deg => rayon projete sin(52 deg) = 0,79 R : le medaillon tient dans la lune, avec un
     anneau de lune nue autour, comme sur la lampe de reference. */
  var PHOTO_LON_SPAN = 104, PHOTO_LAT_SPAN = 104;
  /* Largeur du fondu du medaillon, en fraction de son rayon. */
  var MEDAILLON_FLOU = 0.22;
  // Zone texte (verso, lon 180°)
  var TEXT_LON_SPAN = 84, TEXT_LAT_SPAN = 30;

  function makeCanvas(w, h) { var c = document.createElement('canvas'); c.width = w; c.height = h; return c; }

  function loadImage(src) {
    return new Promise(function (ok, ko) {
      var i = new Image();
      i.crossOrigin = 'anonymous';
      i.onload = function () { ok(i); };
      i.onerror = ko;
      i.src = src;
    });
  }

  function loadFile(file) {
    return new Promise(function (ok, ko) {
      var r = new FileReader();
      r.onload = function () { loadImage(r.result).then(ok, ko); };
      r.onerror = ko;
      r.readAsDataURL(file);
    });
  }

  // Photo de travail : downscale ≤ 1600 px (manipulation fluide), qualité conservée pour la référence
  function prepPhoto(img) {
    var maxSide = 1600, w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    var s = Math.min(1, maxSide / Math.max(w, h));
    var c = makeCanvas(Math.round(w * s), Math.round(h * s));
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return c;
  }

  function tooSmall(img) {
    var w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    return Math.min(w, h) < MIN_PX;
  }

  // Recadrage COVER dans un carré : crop = { cx, cy (0..1, centre), zoom (1 = cover exact), rot (degrés -90..90) }
  // Sous rotation, la fenêtre est élargie du facteur |cos|+|sin| → l'image COUVRE toujours (jamais de coin blanc — pattern portefeuille coverNwForRot)
  /* DEZOOM (09/09/2026, demande fondateur). Deux verrous interdisaient de descendre sous 100 % :
     `Math.max(1, zoom)` et le plafond `win <= min(w,h)`. Consequence : la fenetre de recadrage
     valait au mieux le plus grand CARRE tenant dans la photo — sur une photo 3:4 de telephone,
     25 % de la hauteur etait jetee AVANT que le client touche a quoi que ce soit, sans aucun
     moyen de la recuperer. Il ne pouvait qu'agrandir, donc que couper davantage.
     Desormais la fenetre peut DEPASSER la photo. Ce qui deborde reste transparent : `drawImage`
     clipe le rectangle source ET le rectangle destination dans la meme proportion, donc la photo
     atterrit plus petite, bien placee, et le reste de la toile n'est pas touche. Transparent =
     pas de gravure = lune nue autour du sujet. C'est le comportement voulu, et la chaine de tons
     le supporte deja : `engrave` ne compte QUE les pixels opaques dans son histogramme
     (`p[i+3] >= 128`) et `applyFond` ne fait qu'effacer en `destination-out`.
     Le plancher a d'abord ete fixe a 0,45 — de quoi faire tenir une capture 9:16 entiere dans le
     disque. Il est descendu a 0,15 depuis ; la raison est juste en dessous. */
  /* 0,15 comme le portefeuille (10/09/2026). J'avais mis 0,45 en craignant qu'un sujet trop petit
     donne une gravure illisible. Le fondateur a retourne l'argument, et il a raison : le vrai
     danger est le client dont la photo de visage est TROP SERREE et qui ne peut pas reculer —
     sa gravure sera coupee aux bords. Dezoomer ne degrade rien (la fenetre prend PLUS de pixels
     sources), ca ne fait que reduire la taille du sujet sur la lampe : c'est un choix esthetique.
     Ce qui degrade, c'est de ZOOMER — voir `agrandissement()` plus bas. Une borne de moins qui
     differe entre les deux studios. */
  var ZOOM_MIN = 0.15, ZOOM_MAX = 3;

  /* COURSE DE DEPLACEMENT — demi-amplitude autorisee pour le centre de la fenetre, sur un axe.
     Pourquoi ce n'est pas un simple « la fenetre reste dans la photo » : ce clamp-la donne une
     course NULLE des que la fenetre fait exactement la taille de la photo, c'est-a-dire au zoom
     par defaut. Le client tire sa photo, rien ne bouge, il croit que c'est casse. Le fondateur :
     « il est profane et cherche la facilite, tant de technicite lui compliquerait la vie et il
     risque de partir, il faut que cela soit intuitif ». Une interface qui exige une notice a
     deja echoue — donc LE GESTE DOIT TOUJOURS BOUGER.
     `libre` = la demi-course « propre » (fenetre entierement dans la photo, ou photo entierement
     dans la fenetre : les deux se centrent sur dim/2 et se calculent pareil). On lui donne un
     PLANCHER de 18 % de la fenetre, de sorte qu'il y ait toujours de quoi glisser. Consequence
     assumee : au zoom par defaut, pousser la photo sur le cote decouvre un peu de lune nue —
     exactement ce que fait le portefeuille, valide par le fondateur.
     Propriete a tenir : pour zoom >= 1,5 `libre` domine toujours, donc le cadrage existant ne
     bouge pas d'un pixel. C'est verifie au banc. */
  function courseCadrage(dim, win) {
    return Math.max(Math.abs(win / 2 - dim / 2), win * 0.18);
  }

  /* Borne un cadrage (cx, cy en 0..1) pour une photo donnee. EXPORTE parce que le studio doit
     appliquer EXACTEMENT la meme borne pendant le glissement : sinon le doigt continue de pousser
     une valeur que le moteur refuse, et le client sent une course morte. Une seule source. */
  function clampCrop(photo, crop) {
    var w = photo.width, h = photo.height;
    var rot = (crop.rot || 0) * Math.PI / 180;
    var k = Math.abs(Math.cos(rot)) + Math.abs(Math.sin(rot));
    var base = Math.min(w, h);
    var z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, crop.zoom || 1));
    var win = (base / z) * k;
    if (z >= 1) win = Math.min(base, win);
    var lx = courseCadrage(w, win) / w, ly = courseCadrage(h, win) / h;
    return {
      cx: Math.min(0.5 + lx, Math.max(0.5 - lx, crop.cx != null ? crop.cx : 0.5)),
      cy: Math.min(0.5 + ly, Math.max(0.5 - ly, crop.cy != null ? crop.cy : 0.5))
    };
  }

  /* AGRANDISSEMENT REEL a l'impression. La reference transmise fait REF_PX (1200 px ≈ 10 cm a
     300 DPI) ; la fenetre de cadrage ne preleve que `min(w,h)/zoom` pixels de la photo source.
     Le facteur d'etirement vaut donc REF_PX * zoom / min(w,h).
     Le garde-fou existant (`tooSmall`, MIN_PX 600) ne regarde la photo QU'AU CHARGEMENT et ignore
     completement ce que le recadrage fait ensuite : une photo de 600 px poussee a zoom 3 part
     aujourd'hui en production etiree SIX fois, sans que rien n'alerte. C'est la, et pas dans le
     dezoom, qu'est le risque d'impression moche. */
  function agrandissement(photo, zoom) {
    if (!photo || !photo.width || !photo.height) return 1;
    var z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom || 1));
    return REF_PX * z / Math.min(photo.width, photo.height);
  }
  var AGRANDISSEMENT_MAX = 2.5;   // au-dela, on previent (jamais on ne bloque)

  function drawCovered(ctx, src, size, crop) {
    var w = src.width, h = src.height;
    var rot = (crop.rot || 0) * Math.PI / 180;
    var k = Math.abs(Math.cos(rot)) + Math.abs(Math.sin(rot));
    var base = Math.min(w, h);
    var z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, crop.zoom || 1));
    var win = (base / z) * k;
    /* LE PLAFOND HISTORIQUE RESTE TANT QU'ON NE DEZOOME PAS. Premiere version de ce correctif :
       j'avais retire le plafond `win <= min(w,h)` pour tout le monde. Le banc de non-regression a
       montre qu'a rotation 30° / zoom 1, 80,8 % des pixels changeaient — et en le REGARDANT, le
       nouveau rendu etait moins bon : l'ancien plafond zoomait juste ce qu'il fallait pour masquer
       les coins vides d'une rotation, le mien les revelait. Le fondateur a demande un dezoom, pas
       un changement du comportement de la rotation. On ne leve donc le plafond QUE sous zoom 1. */
    if (z >= 1) win = Math.min(base, win);
    var cx = (crop.cx != null ? crop.cx : 0.5) * w, cy = (crop.cy != null ? crop.cy : 0.5) * h;
    var half = win / 2;
    /* Une seule borne pour les deux regimes, symetrique autour du centre de la photo — voir
       `courseCadrage`. Elle remplace l'ancien `(win >= w) ? w/2 : ...` qui FIGEAIT l'axe des que
       la fenetre atteignait la taille de la photo, c'est-a-dire au zoom par defaut. */
    var lx = courseCadrage(w, win), ly = courseCadrage(h, win);
    cx = Math.min(w / 2 + lx, Math.max(w / 2 - lx, cx));
    cy = Math.min(h / 2 + ly, Math.max(h / 2 - ly, cy));
    ctx.save();
    ctx.translate(size / 2, size / 2);
    ctx.rotate(rot);
    var out = size * k;
    ctx.drawImage(src, cx - half, cy - half, win, win, -out / 2, -out / 2, out, out);
    ctx.restore();
  }

  // ── FOND (portage portefeuille featherToWhite / featherOval, adapté LITHOPHANE) ──
  // mode: 'plein' (rien) | 'doux' (fondu bords ~8%) | 'ovale' (médaillon elliptique)
  // Fondu vers GRIS NEUTRE (198) et non blanc : sur une lithophane, blanc = lumière max (halo brillant) ;
  // le gris = épaisseur moyenne = ton lunaire → la photo FOND dans la lune (vérifié en réel GC28 v2).
  var FOND_C = '198,198,198';
  /* FONDU DE BORD — portage de featherToWhite/featherOval du portefeuille (aa-render-core.js),
     avec LA BONNE POLARITÉ. Là-bas on fond vers le BLANC parce que la composition est en
     `multiply` (blanc × cuir = cuir, la gravure meurt dans la matière). Ici la lune compose en
     `multiply` ÉTEINTE et en `lighten` ALLUMÉE : le blanc est neutre pour l'un, le noir pour
     l'autre. Seule la TRANSPARENCE est neutre pour les deux — on fond donc l'alpha.
     (Avant : on fondait vers un gris 198, très au-dessus de l'émissif de la lune (~52) → au lieu
     de mourir en douceur, le bord fabriquait un halo lumineux. Même faute que le fond blanc.) */
  function applyFond(canvas, mode) {
    if (!mode || mode === 'plein') return canvas;
    var s = canvas.width, x = canvas.getContext('2d');
    x.save();
    x.globalCompositeOperation = 'destination-out'; // l'alpha de la source EFFACE la destination
    if (mode === 'doux') {
      var m = Math.round(s * 0.085);
      var grads = [
        x.createLinearGradient(0, 0, m, 0), x.createLinearGradient(s, 0, s - m, 0),
        x.createLinearGradient(0, 0, 0, m), x.createLinearGradient(0, s, 0, s - m)
      ];
      grads.forEach(function (g, i) {
        g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(1, 'rgba(0,0,0,0)'); // bord effacé → intérieur intact
        x.fillStyle = g;
        if (i === 0) x.fillRect(0, 0, m, s); else if (i === 1) x.fillRect(s - m, 0, m, s);
        else if (i === 2) x.fillRect(0, 0, s, m); else x.fillRect(0, s - m, s, m);
      });
    } else if (mode === 'ovale') {
      /* MEDAILLON ROND — retour fondateur 08/09/2026 : « la photo n'est pas ronde ».
         Le masque etait une ELLIPSE PORTRAIT (`scale(1, 1.18)`) heritee du portefeuille, ou
         un medaillon debout a du sens sur un cuir vertical. Sur une sphere, non : combine a
         l'etirement de la projection, ca donnait un rectangle mou, jamais un medaillon.
         Ici : un CERCLE, avec un fondu franc. Rond sur la lampe = cercle ici + zone carree en
         degres (PHOTO_LON_SPAN == PHOTO_LAT_SPAN). Les deux conditions, pas une seule.
         Au-dela du rayon exterieur, le degrade prolonge sa derniere teinte : les coins du
         carre sont donc entierement effaces, sans avoir a les peindre. */
      var rExt = s * 0.5, rInt = rExt * (1 - MEDAILLON_FLOU);
      var g2 = x.createRadialGradient(s / 2, s / 2, rInt, s / 2, s / 2, rExt);
      g2.addColorStop(0, 'rgba(0,0,0,0)');
      g2.addColorStop(1, 'rgba(0,0,0,1)');
      x.fillStyle = g2; x.fillRect(0, 0, s, s);
    }
    x.restore();
    return canvas;
  }

  /* GOMME MAGIQUE (portage portefeuille) — coups en coordonnées référence (0..1).
     strokes = [ [ {x,y,r}, ... ] , ... ] (un groupe par geste → « Annuler » retire le dernier).
     Elle EFFACE (destination-out) au lieu de peindre en blanc. Avant, frotter posait du blanc pur :
     sur la carte émissive composée en `lighten`, le blanc gagne toujours — donc gommer rendait la
     zone ÉBLOUISSANTE au lieu de la faire disparaître. Exactement l'inverse de « frottez ce qui
     doit disparaître ». Effacer l'alpha, c'est laisser la lune reprendre sa place. */
  function applyEraser(canvas, strokes) {
    if (!strokes || !strokes.length) return canvas;
    var s = canvas.width, x = canvas.getContext('2d');
    x.save();
    x.globalCompositeOperation = 'destination-out';
    x.fillStyle = '#000000'; // seule l'opacité compte en destination-out
    strokes.forEach(function (group) {
      group.forEach(function (pt) {
        x.beginPath(); x.arc(pt.x * s, pt.y * s, pt.r * s, 0, Math.PI * 2); x.fill();
      });
    });
    x.restore();
    return canvas;
  }

  /* PLANCHER DE GRAVURE — remis a 0 le 07/09/2026 (soir).
     Il existait pour compenser `lighten` : sur la carte emissive, tout pixel plus sombre que la
     lune nue (~52/255) perdait contre elle et disparaissait. On relevait donc tout le sujet
     au-dessus du seuil — au prix des noirs, ecrases en gris moyen.
     La composition allumee etant passee en `source-over` (la photo REMPLACE la paroi), le
     probleme n'existe plus : un noir reste noir, comme sur la lithophane de reference. On garde
     la constante comme reglage, a zero. */
  var GRAVURE_PLANCHER = 0;

  /* LUNE ETEINTE — calibres au banc le 07/09/2026 sur la consigne du fondateur :
     « regarde c'est pareil que le texte quand la lampe est eteinte ».

     CIBLE MESUREE : la ou le texte grave marque, il creuse la lune de ~93 niveaux (mesure sur
     buildMaps().map, zone verso, contre l'albedo nu). C'est ca, « pareil que le texte ».

     ALPHA 0,90 — et non 0,50 comme le texte, justement POUR obtenir le meme creux. Un glyphe
       est 100 % encre : a 0,50 il tape deja au maximum. Le ton le plus sombre d'une PHOTO doit
       donc etre pousse plus haut pour atteindre la meme profondeur. Mesure a 0,90 : ~99 niveaux
       au plus creux, contre 93 pour le texte. Meme encre, meme composition, meme creux.
     SOCLE 0,20 — part d'encre posee meme sur les tons CLAIRS. Sans lui, seuls les noirs
       marquaient : sur une photo lumineuse il ne restait que des taches eparses et le fondateur
       ne voyait rien. Une lithophane eteinte montre TOUTE la plaque gravee — la zone entiere est
       une autre matiere, avec l'image en relief dedans. A 0,20, la plaque se lit (~20 niveaux)
       sans noyer le sujet. */
  /* ENCRE NOIRE — correction du 08/09/2026, piste du fondateur, mesuree au banc.
     J'avais pris #3a352c en copiant l'encre du TEXTE au dos, parce qu'il avait dit « c'est
     pareil que le texte ». J'ai copie la couleur au lieu de copier le resultat : un glyphe est
     plein et peut se permettre du brun, une photo a besoin de toute la profondeur disponible.
     Le noir n'agit QUE sur la lune eteinte : allumee, la photo remplace deja la paroi et ses
     noirs sont au plancher (14/255), on ne peut pas descendre plus bas. On corrige donc
     exactement l'etat faible sans toucher a l'autre. Ecart de contraste entre les deux etats :
     37 % -> 7 %. Banc : `banc/encre-noire.mjs`.
     Le TEXTE au dos garde #3a352c (valide par le fondateur, on n'y touche pas). */
  var GRAVURE_ENCRE = '#000000';

  /* Densite : socle 0,30 et opacite 1,00 (mesures du meme banc).
     Au-dela — socle 0,45 — 15 % du medaillon tombe en quasi-noir et la lune devient une tache :
     on gagne 1 point de contraste et on perd l'objet. 0,30 laisse 9 % de noirs. */
  var GRAVURE_ETEINTE_SOCLE = 0.3;
  var GRAVURE_ETEINTE_ALPHA = 1;
  /* Opacite du TRAIT (mode dessin). Mesures du 08/09/2026 : a 0,85 l'ecart entre les deux
     etats est de 7 %, a 0,95 il tombe a 2 % — le dessin se voit pareil, lampe allumee ou
     eteinte, ce que le fondateur demande explicitement. */
  var TRAIT_ALPHA_ALLUMEE = 0.95, TRAIT_ALPHA_ETEINTE = 0.9;

  /* Luminosite seule (mode dessin) : on eclaircit/assombrit le trait sans toucher a l'alpha,
     donc sans jamais reboucher le papier. */
  function luminosite(canvas, b) {
    var ctx = canvas.getContext('2d');
    var im = ctx.getImageData(0, 0, canvas.width, canvas.height), p = im.data, i, v;
    for (i = 0; i < p.length; i += 4) {
      v = (p[i] * 0.2126 + p[i + 1] * 0.7152 + p[i + 2] * 0.0722) * b;
      v = v < 0 ? 0 : (v > 255 ? 255 : v);
      p[i] = p[i + 1] = p[i + 2] = v;
    }
    ctx.putImageData(im, 0, 0);
    return canvas;
  }

  // Auto-optimisation gravure : niveaux de gris (luminance) + étirement d'histogramme (p1–p99) + luminosité utilisateur.
  // La lithophane traduit des NUANCES DE LUMIÈRE → on maximise la dynamique sans écraser les tons moyens.
  // L'ALPHA est préservé intégralement : une zone transparente (détourage, gomme, fondu) doit rester
  // transparente pour laisser la lune traverser — c'est le seul neutre commun à `multiply` et `lighten`.
  function engrave(canvas, bright) {
    var ctx = canvas.getContext('2d');
    var im = ctx.getImageData(0, 0, canvas.width, canvas.height), p = im.data;
    var hist = new Uint32Array(256), i, y, n = 0;
    for (i = 0; i < p.length; i += 4) {
      y = (p[i] * 0.2126 + p[i + 1] * 0.7152 + p[i + 2] * 0.0722) | 0;
      p[i] = y;
      // Histogramme sur le SUJET SEUL : sinon un fond détouré (ou gommé) pèse dans le calcul et
      // écrase la dynamique du visage — le sujet ressortait gris et plat.
      if (p[i + 3] >= 128) { hist[y]++; n++; }
    }
    if (!n) { ctx.putImageData(im, 0, 0); return canvas; } // rien d'opaque : ne rien étirer
    var lo = 0, hi = 255, acc = 0, cut = n * 0.01;
    for (i = 0; i < 256; i++) { acc += hist[i]; if (acc > cut) { lo = i; break; } }
    acc = 0;
    for (i = 255; i >= 0; i--) { acc += hist[i]; if (acc > cut) { hi = i; break; } }
    if (hi - lo < 24) { lo = 0; hi = 255; } // image quasi uniforme : ne pas amplifier le bruit
    var b = bright == null ? 1 : bright, lut = new Uint8ClampedArray(256);
    var etendue = 255 - GRAVURE_PLANCHER;
    for (i = 0; i < 256; i++) {
      var v = GRAVURE_PLANCHER + (i - lo) * etendue / (hi - lo);
      lut[i] = Math.min(255, Math.max(GRAVURE_PLANCHER, v * b));
    }
    for (i = 0; i < p.length; i += 4) { y = lut[p[i]]; p[i] = y; p[i + 1] = y; p[i + 2] = y; }
    ctx.putImageData(im, 0, 0);
    return canvas;
  }

  // LA RÉFÉRENCE : carré 1200², recadrage + gravure + gomme + fond. C'est CETTE image qui part en _aa_photo.
  // opts = { size, fond:'plein'|'doux'|'ovale', strokes:[[{x,y,r}..]..], raw:true (= version couleur SANS gravure, pour l'Avant/Après) }
  function renderReference(photo, crop, bright, opts) {
    opts = opts || {};
    var s = opts.size || REF_PX, c = makeCanvas(s, s), ctx = c.getContext('2d');
    /* PAS de remplissage blanc : sur la carte émissive (`lighten`) le blanc gagne toujours et
       fabriquait un rectangle éblouissant autour du sujet. La toile reste transparente ; le
       recadrage COVER garantit qu'aucun coin ne reste vide quand la photo est opaque. */
    drawCovered(ctx, photo, s, crop || {});
    /* Un DESSIN est deja sa propre courbe de tons : le trait est noir, le papier est vide.
       Lui repasser l'etirement d'histogramme + le plancher de gravure (concus pour rattraper
       une PHOTO) ne ferait que delaver le trait. On saute. La luminosite du client, elle,
       continue de s'appliquer — c'est son reglage, pas une correction automatique. */
    if (opts.trait) { if (bright != null && bright !== 1) luminosite(c, bright); }
    else if (!opts.raw) engrave(c, bright);
    applyEraser(c, opts.strokes);   // la gomme s'applique aussi à l'Avant (cohérence visuelle)
    applyFond(c, opts.fond);        // le fond en dernier (les bords fondus restent propres)
    return c;
  }

  // ── Projection équirectangulaire : dessine `src` centré sur lonCenter (deg), compensation 1/cos(lat)
  //    par tranches horizontales → vu de face sur la sphère, le contenu apparaît NON déformé.
  function drawWarped(ctx, src, lonCenter, lonSpan, latSpan, alpha, comp) {
    var W = ctx.canvas.width, H = ctx.canvas.height;
    var cxPx = ((lonCenter + 180) / 360) * W;
    var zoneW = (lonSpan / 360) * W, zoneH = (latSpan / 180) * H;
    var y0 = H / 2 - zoneH / 2, rows = 160;
    ctx.save();
    if (alpha != null) ctx.globalAlpha = alpha;
    if (comp) ctx.globalCompositeOperation = comp;
    for (var r = 0; r < rows; r++) {
      var yy = y0 + (r / rows) * zoneH, rh = zoneH / rows + 1;
      var lat = ((yy + rh / 2) / H - 0.5) * Math.PI; // -π/2..π/2
      var scale = Math.min(2.2, 1 / Math.max(0.08, Math.cos(lat)));
      var sw = zoneW * scale;
      var sy = (r / rows) * src.height, sh = src.height / rows + 1;
      var x0 = cxPx - sw / 2;
      ctx.drawImage(src, 0, sy, src.width, sh, x0, yy, sw, rh);
      // COUTURE équirectangulaire (lon ±180 = bords de la toile) : re-dessiner de l'autre côté
      // pour que la zone « fasse le tour » — sinon le texte du verso est coupé en deux (bug fondateur GC28 v2)
      if (x0 < 0) ctx.drawImage(src, 0, sy, src.width, sh, x0 + W, yy, sw, rh);
      if (x0 + sw > W) ctx.drawImage(src, 0, sy, src.width, sh, x0 - W, yy, sw, rh);
    }
    ctx.restore();
  }

  /* ENCRER — reprend l'ALPHA d'une reference et lui donne une couleur unie, avec un halo
     optionnel. C'est exactement ce que `textPlate` fait pour le texte : une plaque sombre pour
     la lune eteinte, une plaque blanche avec glow pour la lune allumee. Le dessin merite le
     meme traitement — c'est la reference designee par le fondateur. */
  function encrer(ref, couleur, glow) {
    var w = ref.width, h = ref.height, c = makeCanvas(w, h), x = c.getContext('2d');
    x.drawImage(ref, 0, 0);
    x.globalCompositeOperation = 'source-in';   // ne garde que la silhouette du trait
    x.fillStyle = couleur; x.fillRect(0, 0, w, h);
    if (!glow) return c;
    /* Halo : meme recette que le texte (shadowBlur sur fond transparent). On redessine le trait
       par-dessus son propre flou pour que le halo entoure sans delaver. */
    var g = makeCanvas(w, h), gx = g.getContext('2d');
    gx.shadowColor = 'rgba(255,235,190,0.75)'; gx.shadowBlur = Math.round(w * 0.012);
    gx.drawImage(c, 0, 0);
    gx.shadowBlur = 0; gx.drawImage(c, 0, 0);
    return g;
  }

  /* ENCRER EN TONS CONTINUS — la recette du texte appliquee a une PHOTO.
     Le fondateur a designe le texte au dos comme reference pour la lune ETEINTE :
     « regarde c'est pareil que le texte quand la lampe est eteinte ». Le texte est de l'encre
     la ou il y a un glyphe, du vide ailleurs. Une photo a des milliers de valeurs : on traduit
     donc sa LUMINANCE en OPACITE d'encre — sombre = matiere gravee (opaque), clair = lune nue
     (transparent). Meme encre, meme alpha, meme composition que le texte ; le relief en plus.
     L'alpha d'origine est conserve : une zone gommee ou detouree reste transparente. */
  function encrerTons(ref, couleur) {
    var w = ref.width, h = ref.height, c = makeCanvas(w, h), x = c.getContext('2d');
    x.drawImage(ref, 0, 0);
    var im = x.getImageData(0, 0, w, h), p = im.data, i, y, k;
    var socle = GRAVURE_ETEINTE_SOCLE, plage = 255 * (1 - socle);
    for (i = 0; i < p.length; i += 4) {
      y = p[i] * 0.2126 + p[i + 1] * 0.7152 + p[i + 2] * 0.0722;
      // socle : la plaque entiere se voit ; + relief : plus c'est sombre, plus c'est grave
      k = 255 * socle + (255 - y) * plage / 255;
      p[i + 3] = (k * p[i + 3] / 255) | 0;
    }
    x.putImageData(im, 0, 0);
    x.globalCompositeOperation = 'source-in';
    x.fillStyle = couleur; x.fillRect(0, 0, w, h);
    return c;
  }

  function textPlate(text, color, glow) {
    var c = makeCanvas(900, 320), x = c.getContext('2d');
    x.clearRect(0, 0, 900, 320);
    x.fillStyle = color || '#ffffff'; x.textAlign = 'center'; x.textBaseline = 'middle';
    var fs = 96;
    x.font = 'italic 600 ' + fs + 'px Georgia, "Times New Roman", serif';
    while (fs > 34 && x.measureText(text).width > 820) { fs -= 4; x.font = 'italic 600 ' + fs + 'px Georgia, "Times New Roman", serif'; }
    if (glow !== false) { x.shadowColor = 'rgba(255,255,255,0.55)'; x.shadowBlur = 12; }
    x.fillText(text, 450, 160);
    return c;
  }

  /*
   * Textures de la sphère à partir de : albédo NASA + référence photo + texte verso.
   *  - map (albédo)   : lune éteinte — photo en « secret » (fantôme 7 %), texte discrètement gravé.
   *  - emissive       : lune allumée — la matière glow doucement, la photo se révèle pleine, le texte s'illumine.
   * Teinte LED appliquée par le matériau 3D (emissive color) / par le repli 2D — PAS ici (textures neutres).
   */
  /* POLARITE DE LA REFERENCE — revision du 07/09/2026 (soir), sur la lampe fournisseur montree
     par le fondateur : une PHOTO gravee, tons continus, chaude, qui occupe presque toute la face.

       ALLUMEE : `source-over`. La photo EST la paroi de la lithophane — toute sa gamme transmet.
         `lighten` (l'ancien mode) garde le plus CLAIR des deux ; sur une lune deja claire il ne
         peut PAS produire une zone sombre. Cheveux, vetements et ombres du visage etaient donc
         effaces par la lune : c'est la cause racine du delavage, pas un reglage a ajuster.
       ETEINTE : encre NOIRE (GRAVURE_ENCRE), opacite 1,00, `source-over`, via encrerTons().
         Le fondateur a designe le texte comme reference : « c'est pareil que le texte quand la
         lampe est eteinte ». L'ancien 0,07 en `multiply` etait 7x plus faible que le texte : on
         ne voyait rien, et le commentaire d'alors assumait meme un « secret qui se devine ».

     MODE DESSIN (trait) — revision du 08/09/2026, sur la planche comparative du fondateur :
       « au lieu que le dessin laisse sortir la lumiere je veux l'inverse : que la lumiere
         fasse sortir le dessin, qui est NOIR (que la lampe soit allumee ou eteinte) »
       Ancienne recette : trait BLANC + halo en `lighten` — le trait EMETTAIT, la lune
         restait derriere. Rejete : ce n'est pas ce que fait l'objet.
       Nouvelle recette : trait NOIR en `source-over` dans les DEUX etats. Le trait BLOQUE
         la lumiere, et c'est la lune autour de lui qui le fait ressortir — exactement ce que
         fait la lithophane reelle, ou le trait est de la matiere EN PLUS, donc de l'ombre.
       Pourquoi un DESSIN et pas une photo (arbitrage fondateur du meme jour) : une photo
         encode de la LUMINOSITE, donc un visage a peau foncee devient une masse noire ; un
         dessin encode des CONTOURS, qui restent lisibles quelle que soit la carnation.
       Mesure (banc dessin-inverse.mjs) : ecart eteinte/allumee 5 % -> 2 %, contraste 27 -> 45,
         trait a 8 sur une lune a 225 — le rapport de la lampe fournisseur. */
  var _mapC = null, _emC = null; // RÉUTILISÉS entre rebuilds (évite 16,8 Mo d'allocations par frappe — revue perf GC28)
  function buildMaps(albedoImg, refCanvas, text, opts) {
    var trait = !!(opts && opts.trait);
    if (!_mapC) { _mapC = makeCanvas(TEX_W, TEX_H); _emC = makeCanvas(TEX_W, TEX_H); }
    var map = _mapC, mx = map.getContext('2d');
    mx.clearRect(0, 0, TEX_W, TEX_H);
    mx.drawImage(albedoImg, 0, 0, TEX_W, TEX_H);
    var em = _emC, ex = em.getContext('2d');
    ex.fillStyle = '#000000'; ex.fillRect(0, 0, TEX_W, TEX_H);
    // la résine translucide fait glow TOUTE la lune : albédo atténué = relief lumineux
    ex.globalAlpha = 0.34; ex.drawImage(albedoImg, 0, 0, TEX_W, TEX_H); ex.globalAlpha = 1;
    if (refCanvas) {
      if (trait) {
        /* MEME encre noire, MEME composition dans les deux etats : le trait est de la matiere,
           il ne s'allume pas. Seul l'alpha differe — allumee la lune est plus claire derriere,
           il faut donc un poil plus d'encre pour tenir le meme ecart (0,95 contre 0,90). */
        drawWarped(mx, encrer(refCanvas, GRAVURE_ENCRE, false), 0, PHOTO_LON_SPAN, PHOTO_LAT_SPAN, TRAIT_ALPHA_ETEINTE, 'source-over');
        drawWarped(ex, encrer(refCanvas, GRAVURE_ENCRE, false), 0, PHOTO_LON_SPAN, PHOTO_LAT_SPAN, TRAIT_ALPHA_ALLUMEE, 'source-over');
      } else {
        // éteinte : recette du texte, en tons continus → une gravure qu'on VOIT dans la matière
        drawWarped(mx, encrerTons(refCanvas, GRAVURE_ENCRE), 0, PHOTO_LON_SPAN, PHOTO_LAT_SPAN, GRAVURE_ETEINTE_ALPHA, 'source-over');
        // allumée : la photo EST la paroi — elle remplace la surface avec toute sa gamme de tons
        drawWarped(ex, refCanvas, 0, PHOTO_LON_SPAN, PHOTO_LAT_SPAN, 0.9, 'source-over');
      }
    }
    if (text) {
      // éteinte : gravure DISCRÈTE mais visible → plaque SOMBRE (une plaque blanche en multiply est un no-op — revue GC28)
      drawWarped(mx, textPlate(text, '#3a352c', false), 180, TEXT_LON_SPAN, TEXT_LAT_SPAN, 0.5, 'source-over');
      drawWarped(ex, textPlate(text), 180, TEXT_LON_SPAN, TEXT_LAT_SPAN, 0.95, 'lighten'); // allumée : le texte s'illumine
    }
    return { map: map, emissive: em };
  }

  // ── REPLI 2D « fake 3D » : disque lunaire ombré + photo + halo LED. face: 'front' | 'back'
  function drawFallback(ctx, W, H, o) {
    ctx.clearRect(0, 0, W, H);
    var cx = W / 2, cy = H / 2 - H * 0.03, R = Math.min(W, H) * 0.46; // aligné sur le cadrage 3D plein cadre (retours fondateur 4-5)
    var lit = !!o.lit, color = o.color || '#ffd9a0';
    if (lit) { // halo LED derrière la lune
      var halo = ctx.createRadialGradient(cx, cy, R * 0.5, cx, cy, R * 2.1);
      halo.addColorStop(0, hexA(color, 0.5)); halo.addColorStop(0.55, hexA(color, 0.16)); halo.addColorStop(1, hexA(color, 0));
      ctx.fillStyle = halo; ctx.fillRect(0, 0, W, H);
    }
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.clip();
    // surface : centre de l'albédo équirect pressé dans le disque (approximation frontale honnête)
    if (o.albedo) {
      var aw = o.albedo.width, ah = o.albedo.height;
      ctx.drawImage(o.albedo, aw * 0.28, ah * 0.08, aw * 0.44, ah * 0.84, cx - R, cy - R, R * 2, R * 2);
    } else { ctx.fillStyle = '#b9b4ac'; ctx.fillRect(cx - R, cy - R, R * 2, R * 2); }
    if (lit) { ctx.fillStyle = hexA(color, 0.34); ctx.globalCompositeOperation = 'overlay'; ctx.fillRect(cx - R, cy - R, R * 2, R * 2); ctx.globalCompositeOperation = 'source-over'; }
    if (o.face === 'back' ? o.textPlate : o.ref) {
      var content = o.face === 'back' ? o.textPlate : o.ref;
      var cr = R * 0.78;
      ctx.save();
      ctx.beginPath(); ctx.arc(cx, cy, cr, 0, Math.PI * 2); ctx.clip();
      if (o.trait && o.face !== 'back') {
        // DESSIN — meme polarite que buildMaps (voir le bloc de doctrine) : trait NOIR, jamais emissif.
        content = encrer(content, GRAVURE_ENCRE, false);
        ctx.globalAlpha = lit ? TRAIT_ALPHA_ALLUMEE : TRAIT_ALPHA_ETEINTE;
        ctx.globalCompositeOperation = 'source-over';
      } else if (o.face !== 'back') {
        /* PHOTO — meme polarite que buildMaps : eteinte = recette du texte en tons continus,
           allumee = la photo remplace la paroi. */
        if (!lit) content = encrerTons(content, GRAVURE_ENCRE);
        ctx.globalAlpha = lit ? 0.9 : GRAVURE_ETEINTE_ALPHA;
        ctx.globalCompositeOperation = 'source-over';
      } else {
        ctx.globalAlpha = lit ? 0.92 : 0.10;
        ctx.globalCompositeOperation = lit ? 'lighten' : 'multiply';
      }
      if (o.face === 'back') {
        ctx.fillStyle = '#000'; if (lit) ctx.fillRect(cx - cr, cy - cr, cr * 2, cr * 2);
        var pw = cr * 1.7, ph = pw * (content.height / content.width);
        ctx.drawImage(content, cx - pw / 2, cy - ph / 2, pw, ph);
      } else {
        ctx.drawImage(content, cx - cr, cy - cr, cr * 2, cr * 2);
      }
      /* LA LED PASSE APRES LA PHOTO. Elle est posee sur la lune plus haut (overlay 0,34), mais
         en `source-over` la photo la RECOUVRE : le medaillon ressortait gris au milieu d'une lune
         ambre. On re-teinte donc la zone photo, dans son propre clip. Fidele a l'objet : sur une
         lithophane la lumiere de la LED traverse la photo, elle ne s'arrete pas devant. */
      /* `!o.trait` a saute le 08/09/2026 : tant que le trait EMETTAIT en `lighten`, le
         re-teinter en `multiply` l'aurait eteint. Maintenant qu'il est pose en `source-over`
         comme la photo, il RECOUVRE la teinte LED exactement pareil — donc il la reprend. */
      /* DEGRADE, PAS APLAT (09/09/2026). Un `fillRect` uni s'arretait NET au rayon du medaillon :
         la lune porte `overlay 0.34`, le medaillon portait `multiply 0.8`, et les deux teintes se
         rejoignaient sur une frontiere de 1 px. Mesure au banc (bord-led.html, vignette 300 px,
         cas le plus dur car le corps de lune y est un gris uni) : saut de 9,36 niveaux de luminance
         a r=108, quand le modele spherique alentour ne fait que 0,2 a 1,5. Soit un anneau 6 a 20x
         plus raide que l'ombrage naturel — l'oeil ne voit que lui.
         Le degrade garde la pleine teinte sur le coeur puis la dissout jusqu'a zero EXACTEMENT au
         rayon de clip : plus aucune marche, la LED se fond dans la lune au lieu de s'y decouper.
         Fidele a l'objet : sur une lithophane la lumiere est plus forte au centre de la plaque. */
      if (lit && o.face !== 'back') {
        var led = ctx.createRadialGradient(cx, cy, 0, cx, cy, cr);
        led.addColorStop(0.00, hexA(color, 0.80));
        led.addColorStop(0.58, hexA(color, 0.79));
        led.addColorStop(0.74, hexA(color, 0.70));
        led.addColorStop(0.86, hexA(color, 0.48));
        led.addColorStop(0.95, hexA(color, 0.20));
        led.addColorStop(1.00, hexA(color, 0.00));
        ctx.globalCompositeOperation = 'multiply'; ctx.globalAlpha = 1;
        ctx.fillStyle = led; ctx.fillRect(cx - cr, cy - cr, cr * 2, cr * 2);
      }
      ctx.restore();
    }
    // modelé sphérique (ombre de bord) + reflet doux
    var sh = ctx.createRadialGradient(cx - R * 0.3, cy - R * 0.35, R * 0.2, cx, cy, R);
    sh.addColorStop(0, 'rgba(255,255,255,' + (lit ? 0.06 : 0.10) + ')');
    sh.addColorStop(0.75, 'rgba(0,0,0,0)');
    sh.addColorStop(1, 'rgba(8,10,18,' + (lit ? 0.34 : 0.52) + ')');
    ctx.fillStyle = sh; ctx.fillRect(cx - R, cy - R, R * 2, R * 2);
    ctx.restore();
    // socle stylisé
    var bw = R * 0.9, bh = R * 0.22, by = cy + R * 0.98;
    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    ctx.beginPath(); ctx.ellipse(cx, by + bh * 0.72, bw * 0.85, bh * 0.42, 0, 0, Math.PI * 2); ctx.fill();
    var bg = ctx.createLinearGradient(cx - bw / 2, 0, cx + bw / 2, 0);
    bg.addColorStop(0, '#2a2019'); bg.addColorStop(0.5, '#4a382a'); bg.addColorStop(1, '#241b14');
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.moveTo(cx - bw / 2, by + bh); ctx.quadraticCurveTo(cx - bw / 2, by, cx - bw * 0.34, by);
    ctx.lineTo(cx + bw * 0.34, by); ctx.quadraticCurveTo(cx + bw / 2, by, cx + bw / 2, by + bh);
    ctx.closePath(); ctx.fill();
  }

  function hexA(hex, a) {
    var h = hex.replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
  }

  /* PONT APERÇU → FICHIER D'IMPRESSION.
     La référence porte désormais de la TRANSPARENCE (détourage, gomme, fondu de bord) parce que
     c'est le seul neutre qui marche dans l'aperçu, où la lune compose en `multiply` éteinte et en
     `lighten` allumée. Mais le fichier envoyé au worker est un JPEG : le JPEG n'a pas d'alpha, et
     un canvas transparent exporté en JPEG devient NOIR. Sur une lithophane, noir = épaisseur max =
     un disque opaque. Il faut donc aplatir AVANT d'exporter.
     Sur quoi ? Sur FOND_C (gris 198) — la valeur déjà utilisée par le fondu de bord et notée
     « vérifiée en réel GC28 v2 » comme le ton qui fait FONDRE la photo dans la lune. Transparent
     dans l'aperçu et gris 198 dans le fichier décrivent donc le même fait physique : « ici, on ne
     touche pas à la paroi ». (Hérité d'une vérification antérieure — pas re-mesuré par moi.)
     ⚠ RESTE À TRANCHER avec le fournisseur : sa machine veut-elle ce JPEG aplati, ou un PNG à
     canal alpha ? Tant que la question n'est pas posée, on garde le régime déjà validé en réel. */
  function flattenPrint(canvas) {
    var s = canvas.width, c = makeCanvas(s, canvas.height), x = c.getContext('2d');
    x.fillStyle = 'rgb(' + FOND_C + ')'; x.fillRect(0, 0, s, canvas.height);
    x.drawImage(canvas, 0, 0);
    return c;
  }

  // Vignette panier (aa-cart-preview lit localStorage['aa_case_prev:<uid>']) : lune allumée mini
  function thumb(refCanvas, color) {
    var s = 300, c = makeCanvas(s, s), x = c.getContext('2d');
    x.fillStyle = '#141824'; x.fillRect(0, 0, s, s);
    drawFallback(x, s, s, { ref: refCanvas, lit: true, color: color || '#ffd9a0', face: 'front' });
    try { return c.toDataURL('image/jpeg', 0.85); } catch (e) { return null; }
  }

  /* ZOOM « TOUT AFFICHER » — la valeur exacte pour que la photo ENTIERE tienne dans le cadrage.
     Geometrie : le canvas montre un carre de cote `win/k` = `base/zoom` de l'espace source, pivote
     de `rot`. Faire tenir la photo dedans revient a faire tenir sa boite englobante une fois
     tournee de -rot : W' = w|cos| + h|sin|, H' = w|sin| + h|cos|. D'ou zoom = base / max(W', H').
     A rotation nulle, ca redonne simplement min(w,h) / max(w,h) — 0,75 pour une photo 3:4.
     Sans ce bouton le client devrait chercher la valeur au curseur ; avec, c'est un geste. */
  function zoomTout(photo, rot) {
    if (!photo || !photo.width || !photo.height) return 1;
    var w = photo.width, h = photo.height, a = (rot || 0) * Math.PI / 180;
    var co = Math.abs(Math.cos(a)), si = Math.abs(Math.sin(a));
    var W = w * co + h * si, H = w * si + h * co;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.min(w, h) / Math.max(W, H)));
  }

  window.AAMoon = {
    MIN_PX: MIN_PX, REF_PX: REF_PX,
    ZOOM_MIN: ZOOM_MIN, ZOOM_MAX: ZOOM_MAX, zoomTout: zoomTout,
    clampCrop: clampCrop, agrandissement: agrandissement, AGRANDISSEMENT_MAX: AGRANDISSEMENT_MAX,
    PHOTO_LON_SPAN: PHOTO_LON_SPAN, PHOTO_LAT_SPAN: PHOTO_LAT_SPAN,
    loadImage: loadImage, loadFile: loadFile, prepPhoto: prepPhoto, tooSmall: tooSmall,
    drawCovered: drawCovered, renderReference: renderReference, buildMaps: buildMaps,
    applyFond: applyFond, applyEraser: applyEraser,
    textPlate: textPlate, drawFallback: drawFallback, thumb: thumb, hexA: hexA,
    flattenPrint: flattenPrint
  };
})();
